import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  useTheme,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import TranslateIcon from '@mui/icons-material/Translate';
import RoomServiceIcon from '@mui/icons-material/RoomService';

const Services = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const services = [
    {
      icon: <SchoolIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '留学机构',
      description: '专业处理学历证明、成绩单、推荐信等留学申请材料翻译',
      features: ['学历学位证书', '成绩单翻译', '推荐信翻译', '个人陈述'],
      color: theme.palette.primary.main,
      badge: '机构服务'
    },
    {
      icon: <FlightTakeoffIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '签证机构',
      description: '快速准确翻译签证申请材料，让您的出行更顺畅',
      features: ['护照翻译', '签证申请表', '银行流水', '在职证明', '旅行计划'],
      color: theme.palette.primary.main,
      badge: '机构服务'
    },
    {
      icon: <PublicIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '移民机构',
      description: '专业移民文件翻译服务，确保材料符合移民局要求',
      features: ['出生证明', '婚姻证明', '无犯罪记录', '体检报告', '资产证明'],
      color: theme.palette.primary.main,
      badge: '机构服务'
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '出海企业',
      description: '助力企业产品国际化，提供专业的商业文档翻译',
      features: ['产品说明书', '营销材料', '营业执照', '技术文档', '商业合同'],
      color: theme.palette.primary.main,
      badge: '企业服务'
    },
    {
      icon: <TranslateIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '专业译员',
      description: '为同行译员提供技术支持和质量把控，共同提升行业标准',
      features: ['质量审核服务', '专业术语库', '行业标准培训', '合作翻译'],
      color: theme.palette.primary.main,
      badge: '专业服务'
    },
    {
      icon: <PersonIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: '普通个人',
      description: '为个人用户提供贴心的一对一翻译服务，满足各种生活需求',
      features: ['个人证件翻译', '学历认证', '成绩单', '生活文档'],
      color: theme.palette.primary.main,
      badge: '个人服务'
    }
  ];

  const advantages = [
    '专业译员团队，具备相关领域背景',
    '严格质量控制，多重校对审核',
    '快速交付，支持加急服务',
    '格式保持，确保文档美观',
    '保密协议，信息安全有保障',
    '售后支持，一对一服务修改'
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* 页面标题 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <RoomServiceIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(90deg, #4353FF 0%, #6B78FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 2
          }}
        >
          专业翻译服务
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
        >
          只做一对一的专业级翻译服务
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap'}}>
          <Chip label="留学机构" color="primary" />
          <Chip label="签证机构" color="primary" />
          <Chip label="移民机构" color="primary" />
          <Chip label="出海企业" color="primary" />
          <Chip label="专业译员" color="primary" />
          <Chip label="普通个人" color="primary" />
        </Box>
      </Box>

      {/* 服务类型 */}
      <Grid container spacing={4} sx={{ mb: 8}}>
        {services.map((service, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                p: 2,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: `2px solid transparent`,
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 12px 35px ${service.color}20`,
                  border: `2px solid ${service.color}30`
                }
              }}
            >
              <Chip 
                label={service.badge}
                size="small"
                sx={{ 
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  backgroundColor: service.color + '15',
                  color: service.color,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.4, textAlign: 'center' }}>
                  {service.icon}
                  <Typography variant="h5" sx={{ fontWeight: 600, mt: 1.6, mb: 0.8, color: service.color }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {service.description}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {service.features.map((feature, idx) => (
                    <Chip 
                      key={idx}
                      label={feature}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderColor: theme.palette.grey[300],
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: service.color + '10',
                          borderColor: service.color + '50'
                        }
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 服务优势 */}
      <Box sx={{ mb: 8, mt: 24 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center', 
            mb: 8, 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          我们的服务优势
        </Typography>
        <Card sx={{ p: 4, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                专业服务保障
              </Typography>
              <List>
                {advantages.slice(0, 3).map((advantage, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 20, 
                          color: theme.palette.success.main 
                        }} 
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={advantage}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                质量与服务
              </Typography>
              <List>
                {advantages.slice(3).map((advantage, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 20, 
                          color: theme.palette.success.main 
                        }} 
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={advantage}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* 质量保证 */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          color: theme.palette.primary.main,
          mb: 8,
          mt: 24
        }}
      >
        <VerifiedIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          专业品质保证
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          我们的译员团队具备相关专业背景，熟悉各国签证、移民、留学等申请要求，
          确保翻译文件符合官方标准，助您顺利完成各项申请。
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            navigate('/trans');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(67, 83, 255, 0.3)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(67, 83, 255, 0.4)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          立即开始翻译
        </Button>
      </Card>
    </Container>
  );
};

export default Services; 